/**
 * Clicking where a note already is, with a note type selected, deletes that note instead of placing
 * another (docs/spec/srvr/placement.md SRVR-0338). The test is overlap: if the cursor note sits well on
 * top of an existing note on the current beat, that note is the one meant.
 *
 * Pure geometry, so it is unit-tested under jsdom; the grid's click handler in TunnelScene.tsx asks it.
 */
import type { Note } from '../../types/beatmap';
import { GRID_SCALE } from '../../types/beatmap';

/**
 * How far apart, in world units, the cursor note's centre and an existing note's centre may be for the
 * click to delete it: one note radius, so each centre lies inside the other's ball and the two overlap by
 * about two fifths of their area. Capped at 0.9 of the smaller grid cell, so with a note size larger than
 * a cell, snapping onto the cell next door never reaches the note beside it.
 */
export function deleteReach(noteSize: number, cellW: number, cellH: number): number {
  const cell = Math.min(cellW, cellH);
  return cell > 0 ? Math.min(noteSize, cell * 0.9) : noteSize;
}

/**
 * The index of the existing note on the current beat that the cursor note overlaps, or null. `cursorXY`
 * is the cursor note's cell in note space. Only notes within `beatTol` beats of `playheadBeat` count: a
 * note ahead of or behind the plane is never deleted this way. Of several overlapped, the one whose centre
 * is nearest the cursor's is taken, so one click deletes one note.
 */
export function noteUnderCursor(
  notes: ReadonlyArray<Note>,
  cursorXY: readonly [number, number],
  playheadBeat: number,
  beatTol: number,
  reach: number,
): number | null {
  const scale = GRID_SCALE * 10;
  let best: number | null = null;
  let bestD = reach * reach;
  for (let i = 0; i < notes.length; i++) {
    const n = notes[i];
    if (Math.abs(n.time_beats - playheadBeat) >= beatTol) continue;
    const d = ((n.position[0] - cursorXY[0]) * scale) ** 2 + ((n.position[1] - cursorXY[1]) * scale) ** 2;
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
}
